import React, { useEffect, useState, useMemo } from "react";
import { Helmet } from "react-helmet";
import ContentWrapper from "../../components/contentwrapper/ContentWrapper";
import { Table } from "react-bootstrap";
import { fetchAllDataAPI, filterByParams } from "../../utils/api";
import Skeleton from "react-loading-skeleton";
import { numberFormat } from "../../utils/numberFormat";
import moment from "moment";
import ReportNavBar from "../../components/ReportNavBar";
import withAuth from "../../utils/withAuth";

import { CiExport } from "react-icons/ci";
import CsvDownloader from "react-csv-downloader";
import {
    PieChart,
    Pie,
    Cell,
    Tooltip,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Legend,
    ResponsiveContainer,
    LabelList,
} from "recharts";

const RADIAN = Math.PI / 180;

const renderDonutLabelWithLine = (props) => {
    const {
        cx,
        cy,
        midAngle,
        outerRadius,
        value,
        fill,
        payload,
    } = props;

    const sin = Math.sin(-RADIAN * midAngle);
    const cos = Math.cos(-RADIAN * midAngle);
    const labelText = `${payload.name} (${Number(value).toFixed(1)}%)`;

    const sx = cx + outerRadius * cos;
    const sy = cy + outerRadius * sin;
    const mx = cx + (outerRadius + 10) * cos;
    const my = cy + (outerRadius + 10) * sin;
    const ex = mx + (cos >= 0 ? 22 : -22);
    const ey = my;
    const textAnchor = cos >= 0 ? "start" : "end";

    return (
        <g>
            <polyline
                points={`${sx},${sy} ${mx},${my} ${ex},${ey}`}
                stroke={fill || "#999"}
                strokeWidth={1}
                fill="none"
            />
            <circle cx={ex} cy={ey} r={2} fill={fill || "#999"} />
            <text
                x={ex + (cos >= 0 ? 4 : -4)}
                y={ey}
                textAnchor={textAnchor}
                dominantBaseline="central"
                style={{
                    fontSize: "12px",
                    fontWeight: 600,
                    fill: "#333",
                    letterSpacing: "0.5px",
                }}
            >
                {labelText}
            </text>
        </g>
    );
};

const DONUT_COLORS = [
    "#4285F4",
    "#FBBC05",
    "#34A853",
    "#EA4335",
    "#A142F4",
    "#00ACC1",
    "#f41ed7ff",
    "#7CB342",
    "#35fdb0ff",
    "#8D6E63",
];

const BAR_COLORS = {
    revenue: "#001637",
    delivery: "#FFC000",
};

const formatShortCurrency = (value) => {
    const v = Number(value || 0);

    if (v >= 1e7) {
        return `₹${(v / 1e7).toFixed(1)} Cr`;
    }
    if (v >= 1e5) {
        return `₹${(v / 1e5).toFixed(1)} L`;
    }
    return `₹${v.toLocaleString("en-IN")}`;
};

const formatRevenueWithDelivery = (revenue, deliveryPct) => {
    const revNum = Number(revenue || 0);
    const revStr = numberFormat(revNum);

    if (
        deliveryPct === null ||
        deliveryPct === undefined ||
        isNaN(Number(deliveryPct))
    ) {
        return revStr;
    }

    const pctStr = Number(deliveryPct).toFixed(2);
    return `${revStr} (${pctStr}%)`;
};

const OnlineMedia = () => {
    const defaultStart = moment().subtract(6, "months").startOf("month");
    const defaultEnd = moment().endOf("day");

    const [report, setReport] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    // 'ALL' | 'Online' | 'TV'
    const [selectedMedia, setSelectedMedia] = useState("ALL");

    // still keep a dateRange for info
    const [dateRange, setDateRange] = useState({
        startDate: defaultStart,
        endDate: defaultEnd,
    });

    // NEW: month-only selection (YYYY-MM)
    const [monthRange, setMonthRange] = useState({
        from: defaultStart.format("YYYY-MM"),
        to: defaultEnd.format("YYYY-MM"),
    });

    useEffect(() => {
        getReport();
    }, []);

    const getReport = async () => {
        setIsLoading(true);
        try {
            const res = await fetchAllDataAPI(
                "super-admin-get-online-and-media-performance-report-last-six-months"
            );
            if (res.data.status === 1) {
                setReport(res.data.report || []);
            }
        } catch (err) {
            console.error("Error fetching report:", err);
        }
        setIsLoading(false);
    };

    // NEW: Apply month-only range
    const handleApplyMonthRange = async () => {
        setIsLoading(true);

        // Convert YYYY-MM to actual start/end dates
        const startMoment = moment(monthRange.from + "-01").startOf("month");
        const endMoment = moment(monthRange.to + "-01").endOf("month");

        setDateRange({
            startDate: startMoment,
            endDate: endMoment,
        });

        const payload = {
            startDate: startMoment.format("YYYY-MM-DD"),
            endDate: endMoment.format("YYYY-MM-DD"),
        };

        try {
            const res = await filterByParams(
                "super-admin-get-online-and-media-performance-report-last-six-months",
                payload
            );
            if (res.data.status === 1) {
                setReport(res.data.report || []);
            }
        } catch (err) {
            console.error("Filter fetch error:", err);
        }

        setIsLoading(false);
    };

    const handleReset = async () => {
        setMonthRange({
            from: defaultStart.format("YYYY-MM"),
            to: defaultEnd.format("YYYY-MM"),
        });
        setDateRange({
            startDate: defaultStart,
            endDate: defaultEnd,
        });
        await getReport();
    };

    const monthName = (num) =>
        moment().month(num - 1).format("MMMM").toUpperCase();

    const lastSixMonths = useMemo(() => {
        const keys = [];

        report.forEach((item) => {
            const key = `${item.year_num}-${String(item.month_num).padStart(2, "0")}`;
            if (!keys.includes(key)) keys.push(key);
        });

        const sorted = keys.sort();
        return sorted.slice(-6);
    }, [report]);

    const groupedReport = useMemo(() => {
        const grouped = {};

        report.forEach((item) => {
            const channel = item.chanel_media_type || "Unknown";
            const key = `${item.year_num}-${String(item.month_num).padStart(2, "0")}`;

            if (!grouped[channel]) grouped[channel] = {};

            grouped[channel][key] = {
                revenue: Number(item.month_revenue || 0),
                delivery:
                    item.delivery_percentage != null
                        ? Number(item.delivery_percentage)
                        : null,
            };
        });

        return grouped;
    }, [report]);

    const grandTotals = useMemo(() => {
        const totals = {};
        lastSixMonths.forEach((key) => {
            totals[key] = 0;
        });

        report.forEach((item) => {
            const key = `${item.year_num}-${String(item.month_num).padStart(2, "0")}`;
            if (totals[key] !== undefined) {
                totals[key] += Number(item.month_revenue || 0);
            }
        });

        return totals;
    }, [report, lastSixMonths]);

    const channels = useMemo(
        () => Object.keys(groupedReport),
        [groupedReport]
    );

    const exportColumns = useMemo(() => {
        const cols = [
            { id: "s_no", displayName: "S. No" },
            { id: "source_type", displayName: "Source Type" },
        ];

        lastSixMonths.forEach((key) => {
            const [, m] = key.split("-");
            cols.push({
                id: `col_${key}`,
                displayName: monthName(Number(m)),
            });
        });

        return cols;
    }, [lastSixMonths]);

    const exportRows = useMemo(() => {
        if (!channels.length) return [];

        return channels.map((channel, index) => {
            const row = {
                s_no: index + 1,
                source_type: channel,
            };

            lastSixMonths.forEach((key) => {
                const cell = groupedReport[channel][key];
                row[`col_${key}`] = cell
                    ? formatRevenueWithDelivery(cell.revenue, cell.delivery)
                    : "";
            });

            return row;
        });
    }, [channels, groupedReport, lastSixMonths]);

    const filteredByMedia = useMemo(() => {
        if (selectedMedia === "ALL") return report;
        const sel = selectedMedia.toLowerCase();

        return report.filter(
            (r) => (r.chanel_media_type || "").toLowerCase() === sel
        );
    }, [report, selectedMedia]);

    const donutData = useMemo(() => {
        const data = [];

        lastSixMonths.forEach((key) => {
            const [y, m] = key.split("-");

            const rows = filteredByMedia.filter(
                (r) =>
                    String(r.year_num) === String(y) &&
                    String(r.month_num).padStart(2, "0") === m
            );

            const values = rows
                .map((r) =>
                    parseFloat(
                        String(r.delivery_percentage ?? "")
                            .toString()
                            .replace("%", "")
                    )
                )
                .filter((v) => !isNaN(v));

            if (values.length) {
                const avg =
                    values.reduce((sum, v) => sum + v, 0) / values.length;

                data.push({
                    name: monthName(parseInt(m, 10)),
                    value: Number(avg.toFixed(2)),
                });
            }
        });

        return data;
    }, [filteredByMedia, lastSixMonths]);

    const barData = useMemo(() => {
        const deliveryMap = {};
        donutData.forEach((d) => {
            deliveryMap[d.name] = d.value;
        });

        return lastSixMonths.map((key) => {
            const [, m] = key.split("-");
            const name = monthName(parseInt(m, 10));

            let revenue = 0;
            if (selectedMedia === "ALL") {
                revenue = grandTotals[key] || 0;
            } else if (groupedReport[selectedMedia]) {
                const cell = groupedReport[selectedMedia][key];
                revenue = cell ? cell.revenue || 0 : 0;
            }

            const delivery = deliveryMap[name] || 0;

            return {
                name,
                revenue,
                delivery,
            };
        });
    }, [lastSixMonths, grandTotals, donutData, groupedReport, selectedMedia]);

    return (
        <div className="misDashboard">
            <Helmet>
                <title>SATKARTAR: ONLINE & MEDIA LAST 6 MONTHS</title>
            </Helmet>

            <ContentWrapper>
                <ReportNavBar />

                <div className="col-lg-12 mb-3">
                    <div className="card p-3">
                        <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-3">
                            <h5 className="text-uppercase mb-1">
                                Online &amp; Media – Last 6 Months Revenue
                            </h5>
                            <div className="d-flex gap-3 align-items-center mt-1">
                                <span className="fw-semibold">Media Type:</span>
                                <div className="d-flex gap-2 align-items-center">
                                    <label className="d-flex align-items-center gap-1">
                                        <input
                                            type="radio"
                                            name="mediaType"
                                            value="ALL"
                                            checked={selectedMedia === "ALL"}
                                            onChange={() => setSelectedMedia("ALL")}
                                        />
                                        <span>All</span>
                                    </label>
                                    <label className="d-flex align-items-center gap-1">
                                        <input
                                            type="radio"
                                            name="mediaType"
                                            value="Online"
                                            checked={selectedMedia === "Online"}
                                            onChange={() => setSelectedMedia("Online")}
                                        />
                                        <span>Online</span>
                                    </label>
                                    <label className="d-flex align-items-center gap-1">
                                        <input
                                            type="radio"
                                            name="mediaType"
                                            value="TV"
                                            checked={selectedMedia === "TV"}
                                            onChange={() => setSelectedMedia("TV")}
                                        />
                                        <span>TV</span>
                                    </label>
                                </div>
                            </div>
                            <div className="d-flex align-items-start flex-wrap gap-2">
                                <CsvDownloader
                                    filename={`online_media_6_months_${moment().format(
                                        "YYYY_MM_DD"
                                    )}`}
                                    extension=".csv"
                                    datas={exportRows}
                                    columns={exportColumns}
                                    className="btnorderfilter"
                                >
                                    <CiExport className="svgexport" />
                                    <span
                                        style={{ color: "#fff", border: "none" }}
                                        className="btn btn-info btn-sm btnfilter"
                                    >
                                        Export
                                    </span>
                                </CsvDownloader>

                                <button
                                    type="button"
                                    onClick={handleReset}
                                    className="btn btn-sm btnsecondary btnreset"
                                >
                                    Reset
                                </button>

                                {/* MONTH-ONLY PICKER */}
                                <div className="d-flex align-items-end gap-2">
                                    <div>
                                        <label className="form-label mb-1">From Month</label>
                                        <input
                                            type="month"
                                            className="form-control"
                                            value={monthRange.from}
                                            onChange={(e) =>
                                                setMonthRange((p) => ({
                                                    ...p,
                                                    from: e.target.value,
                                                }))
                                            }
                                            style={{ minWidth: 140 }}
                                        />
                                    </div>
                                    <div>
                                        <label className="form-label mb-1">To Month</label>
                                        <input
                                            type="month"
                                            className="form-control"
                                            value={monthRange.to}
                                            onChange={(e) =>
                                                setMonthRange((p) => ({
                                                    ...p,
                                                    to: e.target.value,
                                                }))
                                            }
                                            style={{ minWidth: 140 }}
                                        />
                                    </div>
                                    <button
                                        type="button"
                                        className="btn btn-md btndaterange"
                                        onClick={handleApplyMonthRange}
                                    >
                                        Apply
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="table-responsive">
                            <Table bordered striped hover className="table-sm">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Source Type</th>
                                        {lastSixMonths.map((key) => {
                                            const [, m] = key.split("-");
                                            return (
                                                <th key={key}>
                                                    {monthName(Number(m))}
                                                </th>
                                            );
                                        })}
                                    </tr>
                                </thead>

                                <tbody>
                                    {isLoading ? (
                                        [...Array(3)].map((_, row) => (
                                            <tr key={row}>
                                                {Array.from({
                                                    length: lastSixMonths.length + 2,
                                                }).map((__, col) => (
                                                    <td key={col}>
                                                        <Skeleton />
                                                    </td>
                                                ))}
                                            </tr>
                                        ))
                                    ) : channels.length ? (
                                        channels.map((channel, index) => (
                                            <tr key={channel}>
                                                <td>{index + 1}</td>
                                                <td
                                                    style={{
                                                        letterSpacing: "0.8px",
                                                        fontWeight: 700,
                                                    }}
                                                >
                                                    {channel}
                                                </td>

                                                {lastSixMonths.map((key) => {
                                                    const cell = groupedReport[channel][key];
                                                    return (
                                                        <td key={key}>
                                                            {cell
                                                                ? formatRevenueWithDelivery(
                                                                    cell.revenue,
                                                                    cell.delivery
                                                                )
                                                                : "—"}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td
                                                colSpan={lastSixMonths.length + 2}
                                                className="text-center"
                                            >
                                                No data available
                                            </td>
                                        </tr>
                                    )}
                                </tbody>

                                {!isLoading && lastSixMonths.length > 0 && (
                                    <tfoot>
                                        <tr
                                            style={{
                                                backgroundColor: "#f8f9fa",
                                                fontWeight: "bold",
                                            }}
                                        >
                                            <td>#</td>
                                            <td className="fw-bold">Grand Total</td>
                                            {lastSixMonths.map((key) => (
                                                <td key={key} className="fw-bold">
                                                    {numberFormat(grandTotals[key] || 0)}
                                                </td>
                                            ))}
                                        </tr>
                                    </tfoot>
                                )}
                            </Table>
                        </div>
                    </div>
                </div>

                <div className="row" style={{ marginLeft: "unset", marginRight: "unset" }}>
                    <div className="col-lg-6 mb-3 card">
                        <div className="d-flex justify-content-center align-items-center py-3">
                            {donutData.length > 0 && (
                                <PieChart width={600} height={320}>
                                    <Pie
                                        data={donutData}
                                        dataKey="value"
                                        nameKey="name"
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={70}
                                        outerRadius={95}
                                        paddingAngle={3}
                                        label={renderDonutLabelWithLine}
                                        labelLine={false}
                                    >
                                        {donutData.map((entry, index) => (
                                            <Cell
                                                key={`cell-${index}`}
                                                fill={
                                                    DONUT_COLORS[index % DONUT_COLORS.length]
                                                }
                                            />
                                        ))}
                                    </Pie>

                                    <text
                                        x="50%"
                                        y="50%"
                                        textAnchor="middle"
                                        dominantBaseline="middle"
                                        fontSize={12}
                                        fill="#333"
                                    >
                                        {selectedMedia === "ALL"
                                            ? "Delivery% (All Media)"
                                            : `Delivery% (${selectedMedia})`}
                                    </text>

                                    <Tooltip
                                        formatter={(v, _name, payload) => [
                                            `${Number(v).toFixed(2)}%`,
                                            payload.payload.name,
                                        ]}
                                    />
                                </PieChart>
                            )}
                        </div>
                    </div>

                    <div className="col-lg-6 mb-3 card">
                        <div className="d-flex justify-content-center align-items-center py-3">
                            {barData.length > 0 && (
                                <ResponsiveContainer width="100%" height={320}>
                                    <BarChart
                                        data={barData}
                                        margin={{ top: 40, right: 25, left: 60, bottom: 30 }}
                                        barCategoryGap={25}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />

                                        <XAxis
                                            dataKey="name"
                                            tick={{ fill: "#111827", fontSize: 11 }}
                                            tickLine={false}
                                            axisLine={{ stroke: "#9CA3AF" }}
                                        />

                                        <YAxis
                                            width={90}
                                            tick={{ fill: "#111827", fontSize: 11 }}
                                            tickLine={false}
                                            axisLine={{ stroke: "#9CA3AF" }}
                                            tickFormatter={(v) =>
                                                `₹${Number(v || 0).toLocaleString("en-IN")}`
                                            }
                                        />

                                        <YAxis
                                            yAxisId="right"
                                            orientation="right"
                                            domain={[0, 100]}
                                            tick={{ fill: "#111827", fontSize: 11 }}
                                            tickFormatter={(v) => `${v}%`}
                                            tickLine={false}
                                            axisLine={{ stroke: "#9CA3AF" }}
                                        />

                                        <Tooltip
                                            contentStyle={{ fontSize: 12 }}
                                            formatter={(value, name) => {
                                                if (name === "Revenue") {
                                                    return [
                                                        formatShortCurrency(value),
                                                        "Revenue",
                                                    ];
                                                }
                                                if (name === "Delivery %") {
                                                    return [
                                                        `${Number(value).toFixed(2)}%`,
                                                        "Delivery %",
                                                    ];
                                                }
                                                return value;
                                            }}
                                            labelStyle={{ color: "#111827", fontWeight: 600 }}
                                        />

                                        <Legend
                                            wrapperStyle={{ paddingTop: 8 }}
                                            formatter={(value) => (
                                                <span
                                                    style={{
                                                        color: "#111827",
                                                        fontSize: 12,
                                                        fontWeight: 500,
                                                    }}
                                                >
                                                    {value}
                                                </span>
                                            )}
                                        />

                                        <Bar
                                            dataKey="revenue"
                                            name="Revenue"
                                            fill={BAR_COLORS.revenue}
                                            radius={[4, 4, 0, 0]}
                                            maxBarSize={30}
                                        >
                                            <LabelList
                                                dataKey="revenue"
                                                position="top"
                                                formatter={(v) => formatShortCurrency(v)}
                                                style={{
                                                    fill: "#111827",
                                                    fontSize: 10,
                                                    fontWeight: 600,
                                                    paintOrder: "stroke",
                                                    stroke: "#ffffff",
                                                    strokeWidth: 3,
                                                }}
                                                offset={6}
                                            />
                                        </Bar>

                                        <Bar
                                            yAxisId="right"
                                            dataKey="delivery"
                                            name="Delivery %"
                                            fill={BAR_COLORS.delivery}
                                            maxBarSize={24}
                                        >
                                            <LabelList
                                                dataKey="delivery"
                                                position="top"
                                                formatter={(v) =>
                                                    `${Number(v || 0).toFixed(1)}%`
                                                }
                                                style={{
                                                    fill: "#111827",
                                                    fontSize: 10,
                                                    fontWeight: 600,
                                                    paintOrder: "stroke",
                                                    stroke: "#ffffff",
                                                    strokeWidth: 3,
                                                }}
                                                offset={4}
                                            />
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            )}
                        </div>
                    </div>
                </div>
            </ContentWrapper>
        </div>
    );
};

export default withAuth(OnlineMedia);
